import React, { useState } from 'react';
import classes from "./PatientForm.module.css";
import { Form, useNavigate, useNavigation, useActionData } from "react-router-dom";
import { FaIdCard, FaAddressCard, FaPhoneAlt, FaMapMarkerAlt, FaSave, FaTimes } from 'react-icons/fa';
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRef } from 'react';
import ConfirmModal from '../../UI/ConfirmModal';
import { useSubmit } from 'react-router-dom';
import { calculateAgeDetailed } from '../../../util/ageCalculator';

const calculateAge = (dobString) => {
  const { years } = calculateAgeDetailed(dobString);
  return years;
};

const PatientForm = ({ method }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = useActionData();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';
  const submit = useSubmit();
  const formRef = useRef();

  const [age, setAge] = useState("");
  const [ageText, setAgeText] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const dobChangeHandler = (event) => {
    const newDob = event.target.value;
    const { years, text } = calculateAgeDetailed(newDob);
    setAge(years);
    setAgeText(text);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setShowConfirmModal(true);
  };

  const confirmRegistrationHandler = () => {
    setShowConfirmModal(false);
    if (formRef.current) {
      submit(formRef.current);
    }
  };

  function cancelHandler() {
    navigate("..");
  }

  return (
    <div className={classes.cardview}>
      <Form method='post' className={classes.form} onSubmit={handleSubmit} ref={formRef}>

        <div className={classes.sectionHeader}>
          <FaIdCard /> {t("Personal Information")}
        </div>

        <div className="row">
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="firstName">{t("First Name")} <span className={classes.reqSign}>*</span></label>
            <input id="firstName" type="text" name="firstName" required />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="middleName">{t("Middle Name")}</label>
            <input id="middleName" type="text" name="middleName" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="lastName">{t("Last Name")} <span className={classes.reqSign}>*</span></label>
            <input id="lastName" type="text" name="lastName" required />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="nickName">{t("Nick Name")}</label>
            <input id="nickName" type="text" name="nickName" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="gender">{t("Gender")} <span className={classes.reqSign}>*</span></label>
            <select id="gender" name="gender" required>
              <option value="male">{t("Male")}</option>
              <option value="female">{t("Female")}</option>
              <option value="other">{t("Other")}</option>
            </select>
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="dob">{t("Date of Birth")}</label>
            <input id="dob" type="date" name="dob" onChange={dobChangeHandler} />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="age">{t("Age (Years)")}</label>
            <div className="d-flex align-items-center gap-2">
              <input id="age" type="number" name="age" value={age} onChange={(e) => setAge(e.target.value)} style={{ flex: 1 }} />
              {ageText && <span className="text-primary fw-bold small" style={{ whiteSpace: 'nowrap' }}>{ageText}</span>}
            </div>
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="maritalStatus">{t("Marital Status")}</label>
            <select id="maritalStatus" name="maritalStatus">
              <option value="single">{t("Single")}</option>
              <option value="married">{t("Married")}</option>
              <option value="divorced">{t("Divorced")}</option>
              <option value="widowed">{t("Widowed")}</option>
            </select>
          </div>
        </div>

        <div className={classes.sectionHeader}>
          <FaAddressCard /> {t("Identity & Background")}
        </div>
        <div className="row">
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="religion">{t("Religion")}</label>
            <input id="religion" type="text" name="religion" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="caste">{t("Caste")}</label>
            <input id="caste" type="text" name="caste" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="occupation">{t("Occupation")}</label>
            <input id="occupation" type="text" name="occupation" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="panCardNo">{t("PAN Card No")}</label>
            <input id="panCardNo" type="text" name="panCardNo" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="adharCardNo">{t("Aadhar Card No")}</label>
            <input id="adharCardNo" type="text" name="adharCardNo" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="identificationMark">{t("Identification Mark")}</label>
            <input id="identificationMark" type="text" name="identificationMark" />
          </div>
        </div>

        <div className={classes.sectionHeader}>
          <FaPhoneAlt /> {t("Contact Information")}
        </div>
        <div className="row">
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="mobile">{t("Mobile Number")}</label>
            <input id="mobile" type="tel" name="mobile" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="alternateMobileNum">{t("Alt Mobile No")}</label>
            <input id="alternateMobileNum" type="tel" name="alternateMobileNum" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="emailId">{t("Email")}</label>
            <input id="emailId" type="email" name="emailId" />
          </div>
        </div>

        <div className={classes.sectionHeader}>
          <FaMapMarkerAlt /> {t("Address & Reference")}
        </div>
        <div className="row">
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="address">{t("Address")} <span className={classes.reqSign}>*</span></label>
            <input id="address" type="text" name="address" required />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="city">{t("City")}</label>
            <input id="city" type="text" name="city" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="pincode">{t("Pincode")}</label>
            <input id="pincode" type="text" name="pincode" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="referredBy">{t("Referred By")}</label>
            <input id="referredBy" type="text" name="referredBy" />
          </div>
          <div className="col-md-4 col-6">
            <label className={classes.label} htmlFor="registrationNo">{t("Registration No")}</label>
            <input id="registrationNo" type="text" name="registrationNo" />
          </div>
        </div>

        {data && data.errorMsg && (
          <div className="alert alert-danger mt-3 text-center">
            {data.errorMsg === "Patient Already Exists" ? t("Patient already exists in the system.") : t(data.errorMsg)}
          </div>
        )}

        <div className={classes.actions}>
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <FaSave className="me-2" />
            )}
            {isSubmitting ? t("Submitting...") : t('Register Patient')}
          </button>
          <button
            type="button"
            onClick={cancelHandler}
            className={classes.secondaryBtn}
          >
            <FaTimes className="me-2" /> {t("Cancel")}
          </button>
        </div>
      </Form>
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={confirmRegistrationHandler}
        title={t("Register Patient")}
        message={t("Are you sure you want to register this patient?")}
        variant="primary"
        confirmText={t("Confirm Registration")}
      />
    </div>
  );
};

export default PatientForm;
